import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  MapPin,
  Footprints,
  Ruler,
  ChevronRight,
  Info,
  FileText,
  ExternalLink,
} from "lucide-react";
import { findAreaBySlug } from "@/data/areas";
import { TRAILS_BY_AREA, trailSlug } from "@/data/trails";
import { usePageMeta } from "@/utils/usePageMeta";
import AndringsforslagButton from "@/components/AndringsforslagButton";

/**
 * Generisk områdesöversikt för friluftsområden (vandringsleder).
 *
 * Driven helt av /app/frontend/src/data/areas.js – nya områden läggs till
 * genom att en post med {slug, mapImage, mapImageType, downloadUrl, trailAreas,
 * pois, googleMaps, …} läggs till där. Ingen ny komponent behövs.
 *
 * Routes: /friluftsliv/omrade/:areaSlug
 */
export default function AreaOverview() {
  const { areaSlug } = useParams();
  const area = findAreaBySlug(areaSlug);

  usePageMeta({
    title: area
      ? `${area.title} – Vandringsleder – vidalv.live`
      : "Vandringsområde",
    description: area?.intro?.slice(0, 160),
    path: `/friluftsliv/omrade/${areaSlug}`,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [areaSlug]);

  if (!area) {
    // Bakåt-kompatibilitet: gamla länkar till /silsjonas redirectas via App.js.
    return <Navigate to="/friluftsliv" replace />;
  }

  // Samla alla leder från trailAreas-nycklarna i ordning.
  const leds = (area.trailAreas || []).flatMap((key) =>
    (TRAILS_BY_AREA[key] || []).map((led) => ({ ...led, area: key }))
  );

  // Download-URL: explicit angiven, annars samma som hero-bilden.
  const downloadUrl = area.downloadUrl || area.mapImage;
  const gmapsUrl = `https://www.google.com/maps/search/?api=1&query=${area.googleMaps.lat},${area.googleMaps.lng}`;

  return (
    <div
      data-testid={`area-overview-${area.slug}`}
      className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14"
    >
      <Link
        to="/friluftsliv"
        data-testid="link-back-friluftsliv"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-bark hover:text-pine no-underline transition-colors mb-6"
      >
        <ArrowLeft size={14} /> Tillbaka till Friluftsliv
      </Link>

      <div className="flex items-center gap-2 mb-1.5">
        <Footprints size={18} className="text-pine" />
        <p className="font-body text-[11px] font-semibold text-bark/55 uppercase tracking-[0.22em]">
          {area.breadcrumb}
        </p>
      </div>

      <h1
        data-testid="area-overview-title"
        className="font-fraktur-smooth text-3xl sm:text-4xl text-falu tracking-wide leading-tight mb-3"
      >
        {area.title}
      </h1>

      <p className="text-base text-bark leading-relaxed mb-6 max-w-2xl">
        {area.intro}
      </p>

      {/* Kartbild – stor förhandsvisning */}
      <figure
        data-testid="area-karta-figure"
        className="mb-5 rounded-sm overflow-hidden border border-warm-border bg-bone-alt"
      >
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="area-karta-bild-link"
          className="block relative"
          title="Öppna kartan i full storlek"
        >
          <img
            src={area.mapImage}
            alt={`Översiktskarta över ${area.title}. Källa: ${area.sourceName}.`}
            data-testid="area-karta-bild"
            className="w-full h-auto block hover:opacity-95 transition-opacity"
            loading="lazy"
          />
          {area.mapImageType === "pdf" && (
            <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-falu text-parchment px-2 py-1 rounded-sm shadow-sm">
              <FileText size={11} /> PDF
            </span>
          )}
        </a>
        <figcaption className="text-[11px] text-bark/55 px-3 py-2 border-t border-warm-border bg-parchment">
          Klicka på bilden för att öppna kartan i full storlek. Källa:{" "}
          <a
            href={area.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-pine hover:text-pine-hover"
          >
            {area.sourceName}
          </a>
        </figcaption>
      </figure>

      {/* Action-knappar */}
      <div className="flex flex-wrap gap-2 mb-8">
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          download={area.downloadName}
          data-testid="area-ladda-ner-karta"
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-body font-semibold text-parchment bg-pine hover:bg-pine-hover px-4 py-2.5 rounded-sm no-underline transition-colors"
        >
          <Download size={14} /> Ladda ner karta
          {area.mapImageType === "pdf" && (
            <span className="opacity-80">(PDF)</span>
          )}
        </a>
        <a
          href={gmapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="area-visa-pa-karta"
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-body font-semibold text-pine bg-parchment border border-pine/40 hover:bg-pine/5 px-4 py-2.5 rounded-sm no-underline transition-colors"
        >
          <MapPin size={14} /> Visa på karta ({area.googleMaps.label})
        </a>
      </div>

      {/* Leder */}
      {leds.length > 0 && (
        <>
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3 border-b border-warm-border/60 pb-1">
            {leds.length === 1
              ? "En markerad led"
              : `${leds.length} markerade leder att välja mellan`}
          </h2>
          <div
            data-testid="area-led-grid"
            className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6"
          >
            {leds.map((led) => (
              <Link
                key={`${led.area}-${led.namn}`}
                to={`/friluftsliv/led/${trailSlug(led.namn)}`}
                data-testid={`area-led-${trailSlug(led.namn)}`}
                className={`group relative bg-parchment border border-warm-border rounded-sm p-4 no-underline block hover:border-pine/60 hover:shadow-sm transition-all ${
                  led.hero ? "sm:col-span-2 border-gold/50" : ""
                } ${led.status === "stängd" ? "opacity-70" : ""}`}
              >
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h3 className="font-heading text-base font-semibold text-ink leading-snug group-hover:text-pine transition-colors">
                    {led.namn}
                  </h3>
                  <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-body font-semibold text-pine bg-pine/10 px-2 py-0.5 rounded-sm">
                    <Ruler size={10} /> {led.km} km
                  </span>
                </div>
                {led.status === "stängd" && (
                  <p className="text-[11px] font-semibold text-falu uppercase tracking-wider mb-1">
                    ⚠ Stigen är stängd
                  </p>
                )}
                <p className="text-sm text-bark leading-relaxed line-clamp-3">
                  {led.text}
                </p>
                <p className="mt-2 inline-flex items-center gap-1 text-[11px] font-body font-semibold text-pine group-hover:text-pine-hover">
                  <MapPin size={11} /> Visa detalj &amp; karta{" "}
                  <ChevronRight size={11} />
                </p>
              </Link>
            ))}
          </div>
        </>
      )}

      {/* POIs / Legend-objekt */}
      {area.pois && area.pois.length > 0 && (
        <div
          data-testid="area-pois"
          className="mb-6 bg-parchment border border-warm-border rounded-sm p-4"
        >
          <h3 className="font-heading text-sm font-semibold text-ink uppercase tracking-wider mb-2">
            På kartan finns även
          </h3>
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
            {area.pois.map((poi) => (
              <li
                key={poi.key}
                data-testid={`area-poi-${poi.key}`}
                className="text-sm text-bark flex items-center gap-1.5"
              >
                <span
                  aria-hidden
                  className="inline-block w-1.5 h-1.5 rounded-full bg-pine/60"
                />
                {poi.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Säkerhets-/respekt-notis */}
      <div
        data-testid="area-notis"
        className="bg-bone-alt border border-warm-border rounded-sm p-4 flex items-start gap-3 mb-6"
      >
        <Info size={16} className="text-pine shrink-0 mt-0.5" />
        <p className="text-sm text-bark leading-relaxed">{area.notis}</p>
      </div>

      <p className="text-[11px] text-bark/55 italic">
        Kartdata och beskrivningar är hämtade från{" "}
        <a
          href={area.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-pine hover:text-pine-hover inline-flex items-center gap-1"
        >
          {area.sourceName} <ExternalLink size={10} />
        </a>{" "}
        (Fjällsjö Framtid). Bilden speglas i vår egen lagring för stabilitet.
      </p>
      <div className="mt-6 pt-4 border-t border-warm-border/70">
        <AndringsforslagButton typ="omrade" referensId={areaSlug} referensNamn={area.name || areaSlug} />
      </div>
    </div>
  );
}
